
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, X, Send, User, Bot } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAssistantConfig } from "@/hooks/useAssistantConfig";

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

const VirtualAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  const { config, responses, quickActions, loading } = useAssistantConfig();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inicializar con mensaje de saludo cuando la configuración esté lista
  useEffect(() => {
    if (config && messages.length === 0) {
      setMessages([{
        id: "1",
        text: config.greeting_message,
        isBot: true,
        timestamp: new Date()
      }]);
    }
  }, [config]);

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Simular respuesta del bot
    setTimeout(() => {
      const botResponse = generateBotResponse(inputMessage.toLowerCase());
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse.text,
        isBot: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      
      if (botResponse.action) {
        setTimeout(() => {
          botResponse.action();
        }, 1000);
      }
    }, 1000);

    setInputMessage("");
  };

  const generateBotResponse = (input: string) => {
    // Buscar respuesta en la base de datos
    for (const response of responses) {
      const hasKeyword = response.keywords.some(keyword => 
        input.includes(keyword.toLowerCase())
      );
      
      if (hasKeyword) {
        return {
          text: response.response_text,
          action: response.action_type === 'navigate' && response.action_value 
            ? () => navigate(response.action_value!) 
            : null
        };
      }
    }

    // Respuesta por defecto si no encuentra coincidencias
    return {
      text: "Entiendo tu consulta. Te puedo ayudar con información sobre nuestros servicios, proyectos, precios o contacto. ¿Qué te gustaría saber específicamente?",
      action: null
    };
  };

  const handleQuickAction = (responseKey: string) => {
    // Buscar la respuesta correspondiente por clave
    const response = responses.find(r => 
      r.keywords.includes(responseKey)
    );
    
    if (response) {
      const botMessage: Message = {
        id: Date.now().toString(),
        text: response.response_text,
        isBot: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      
      if (response.action_type === 'navigate' && response.action_value) {
        setTimeout(() => {
          navigate(response.action_value!);
        }, 1000);
      }
    }
  };

  if (loading) {
    return null; // No mostrar el asistente mientras carga
  }

  if (!config || !config.is_active) {
    return null; // No mostrar si está desactivado
  }

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-techno-purple to-techno-accent p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 animate-bounce hover:animate-none hover:scale-110"
          aria-label="Abrir asistente virtual"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="bg-gradient-to-r from-techno-purple to-techno-accent p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">{config.assistant_name}</h3>
                <p className="text-xs text-white/80">{config.assistant_description}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors duration-200"
              aria-label="Cerrar asistente"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isBot ? 'justify-start' : 'justify-end'} animate-fade-in`}
              >
                <div className={`flex items-start space-x-2 max-w-[85%] ${message.isBot ? '' : 'flex-row-reverse space-x-reverse'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${message.isBot ? 'bg-techno-purple' : 'bg-techno-accent'}`}>
                    {message.isBot ? <Bot className="w-4 h-4 text-white" /> : <User className="w-4 h-4 text-white" />}
                  </div>
                  <div className={`p-3 rounded-2xl shadow-sm ${message.isBot ? 'bg-white border border-gray-200 text-gray-800' : 'bg-techno-purple text-white'}`}>
                    <p className="text-sm whitespace-pre-line leading-relaxed">{message.text}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {quickActions.length > 0 && (
            <div className="p-3 bg-white border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-3 font-medium">Acciones rápidas:</p>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.response_key)}
                    className="text-xs bg-gradient-to-r from-techno-purple/10 to-techno-accent/10 hover:from-techno-purple/20 hover:to-techno-accent/20 text-techno-purple font-medium px-3 py-2 rounded-lg transition-all duration-200 border border-techno-purple/20 hover:border-techno-purple/40"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Escribe tu mensaje..."
                className="flex-1 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-techno-purple focus:border-transparent transition-all duration-200"
              />
              <Button
                onClick={handleSendMessage}
                size="sm"
                className="bg-gradient-to-r from-techno-purple to-techno-accent hover:from-techno-purple/90 hover:to-techno-accent/90 shadow-lg"
                disabled={!inputMessage.trim()}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VirtualAssistant;
